/**
 * Quantity -> live discounted price.
 *
 * Shopify evaluates automatic discounts (the M7 bulk tiers) only against a
 * cart, so a product page cannot know the discounted number on its own. To
 * price a quantity without leaving anything behind, this briefly sets the
 * quantity on the cart, reads the price Shopify returns, then restores the
 * cart to exactly what it was. Nothing is added, the cart badge does not
 * move, the cart drawer never opens, and "Add to cart" behaves as normal.
 *
 * No discount rules live in the theme, so whatever the merchant edits in
 * Admin > Discounts is reflected automatically, in every currency.
 *
 * Quantity 1 never touches the cart at all -- no bulk tier applies there.
 *
 * The currency symbol and locale come from Shopify's own first render -- we
 * only swap the digits -- so any active market ($/Rs./EUR/GBP) is preserved.
 *
 * Runs in every place the price block is rendered: the product page and the
 * quick-add popup, which clones the product page's markup into a dialog.
 * Both can be on screen at once (quick add inside product recommendations),
 * so every lookup is scoped to one "context" -- the product section, or the
 * quick-add modal -- and reads the quantity, variant and price of that
 * context only.
 */
(function () {
  'use strict';

  // The script is loaded both by the price block (product page) and by the
  // quick-add modal snippet, so on a product page with quick add it lands
  // twice. Wire the listeners once.
  if (window.__customPriceQuantityLoaded) return;
  window.__customPriceQuantityLoaded = true;

  const TAG = '[qty-price]';
  const ROOT_SELECTOR = 'product-price[data-discount-price]';
  const QTY_INPUT_SELECTOR = 'input[name="quantity"]';
  const VARIANT_INPUT_SELECTOR = 'form[data-type="add-to-cart-form"] input[name="id"]';
  const CUSTOM_PICKER_SELECTOR = 'variant-picker.custom-variant-picker';

  /** The two places a price block can live. */
  const MODAL_SELECTOR = '#quick-add-modal-content';
  const SECTION_SELECTOR = '.shopify-section';

  /** Below this quantity we leave the cart alone entirely. */
  const SYNC_MIN_QTY = 2;
  const DEBOUNCE_MS = 200;
  const LOADING_CLASS = 'qty-price-loading';
  const SAVINGS_CLASS = 'qty-price-savings';
  const DEFAULT_SAVINGS_LABEL = 'Saved';

  const TAG_ICON =
    '<svg class="' +
    SAVINGS_CLASS +
    '__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"' +
    ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.83 0L3.4 13.43A2 2 0 0 1 2.83 12V4.83a2 2 0 0 1 2-2H12' +
    'a2 2 0 0 1 1.41.58l7.18 7.17a2 2 0 0 1 0 2.83Z"/>' +
    '<circle cx="7.6" cy="7.6" r="1.25" fill="currentColor" stroke="none"/></svg>';

  const routes = (window.Theme && window.Theme.routes) || {};
  const CART_URL = routes.cart_url || '/cart';
  const CART_UPDATE_URL = routes.cart_update_url || '/cart/update';

  /** Serialises cart writes so rapid clicks -- in either context -- can't race. */
  let chain = Promise.resolve();
  /**
   * Set while the cart is temporarily holding a probe quantity.
   * @type {{ variantId: string, baseline: number } | null}
   */
  let pendingRestore = null;

  /**
   * Per-context debounce timer and sequence number. The sequence is bumped on
   * every quantity change: a cart round trip started for an older quantity
   * must not paint over a newer one -- that's what made 5 -> 1 keep showing
   * the five-unit price.
   * @type {WeakMap<Element, { seq: number, timer: number | undefined }>}
   */
  const contextState = new WeakMap();

  /**
   * Shopify's own rendered text for a price element, used as the template we
   * swap digits into. Keyed by the element itself, so the nodes product-price
   * swaps in on a variant change start out fresh with no bookkeeping.
   * @type {WeakMap<Element, { originalText: string, fmt: object|null }>}
   */
  const formatCache = new WeakMap();

  /* ------------------------------------------------------------------ *
   * Contexts
   * ------------------------------------------------------------------ */

  /** @param {Element} ctx */
  function stateFor(ctx) {
    let state = contextState.get(ctx);
    if (!state) {
      state = { seq: 0, timer: undefined };
      contextState.set(ctx, state);
    }
    return state;
  }

  /**
   * The container that owns a node: the quick-add modal when the node is
   * inside it, otherwise its section.
   * @param {EventTarget | null} node
   * @returns {Element | null}
   */
  function containerOf(node) {
    if (!(node instanceof Element)) return null;
    return node.closest(MODAL_SELECTOR) || node.closest(SECTION_SELECTOR) || document.body;
  }

  /** Every container currently holding a discount-enabled price block. */
  function allContexts() {
    const found = [];
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const ctx = containerOf(root);
      if (ctx && found.indexOf(ctx) === -1) found.push(ctx);
    });
    return found;
  }

  /**
   * The context an event belongs to.
   *
   * Normally that is simply the container the event came from. When the event
   * comes from outside any price block -- a buy-buttons block the theme
   * renders in its own section, say -- and the page has exactly one price
   * block, fall back to it rather than ignoring the change. With the quick-add
   * popup open there are two, and then scoping is strict so the two never
   * paint over each other.
   *
   * @param {EventTarget | null} target
   * @returns {Element | null}
   */
  function contextFor(target) {
    const container = containerOf(target);
    if (container && container.querySelector(ROOT_SELECTOR)) return container;

    const contexts = allContexts();
    return contexts.length === 1 ? contexts[0] : null;
  }

  /* ------------------------------------------------------------------ *
   * DOM readers -- all scoped to one context
   * ------------------------------------------------------------------ */

  /**
   * A product page renders more than one buy-buttons block, so there is more
   * than one quantity input in the section. Prefer whichever is actually on
   * screen rather than simply the first in source order.
   * @param {Element} ctx
   */
  function getQuantityInput(ctx) {
    const inputs = Array.prototype.slice.call(ctx.querySelectorAll(QTY_INPUT_SELECTOR));
    if (!inputs.length) return null;
    return inputs.find((el) => el.offsetParent !== null) || inputs[0];
  }

  /** @param {Element} ctx */
  function getQuantity(ctx) {
    const input = getQuantityInput(ctx);
    if (!input) return 1;
    const value = parseInt(input.value, 10);
    return value > 0 ? value : 1;
  }

  /** @param {Element} ctx */
  function getVariantId(ctx) {
    const input = ctx.querySelector(VARIANT_INPUT_SELECTOR);
    return input && input.value ? String(input.value) : null;
  }

  /**
   * The custom variant picker blanks the variant input until a size is chosen.
   * Reuse its own guard so the toast, inline error and field highlight match
   * exactly what the customer gets from Add to cart.
   * @param {Element} ctx
   * @returns {boolean} true when a size is selected (or there's no such picker)
   */
  function requireVariantSelection(ctx) {
    const picker = ctx.querySelector(CUSTOM_PICKER_SELECTOR);
    if (picker && typeof picker.requireSelection === 'function') return picker.requireSelection();
    return true;
  }

  function getVisiblePriceEl(root) {
    const regular = root.querySelector('.price__regular:not(.price__hidden) .price');
    if (regular) return regular;
    const sale = root.querySelector('.price__sale:not(.price__hidden) .price-item--sale.price');
    if (sale) return sale;
    return root.querySelector('.price');
  }

  /* ------------------------------------------------------------------ *
   * Number formatting -- keeps Shopify's currency symbol and separators
   * ------------------------------------------------------------------ */

  /**
   * @param {string} text
   * @returns {{ decSep: string|null, thouSep: string, decimals: number } | null}
   */
  function detectNumberFormat(text) {
    const match = String(text || '').match(/[\d][\d.,\s]*/);
    if (!match) return null;
    const numStr = match[0].trim();

    const lastDot = numStr.lastIndexOf('.');
    const lastComma = numStr.lastIndexOf(',');

    let decSep = null;
    let decimals = 0;

    if (lastDot === -1 && lastComma === -1) {
      decSep = null;
    } else if (lastDot > lastComma) {
      decSep = '.';
      decimals = numStr.length - lastDot - 1;
    } else {
      decSep = ',';
      decimals = numStr.length - lastComma - 1;
    }

    if (decimals > 4) decimals = 0;
    if (decimals === 3 && (numStr.match(/[.,]/g) || []).length > 1) decimals = 0;

    const thouSep = decSep === '.' ? ',' : '.';
    return { decSep, thouSep, decimals };
  }

  /**
   * @param {number} cents
   * @param {{ decSep: string|null, thouSep: string, decimals: number } | null} fmt
   * @returns {string}
   */
  function formatNumberOnly(cents, fmt) {
    if (!fmt) return (cents / 100).toFixed(2);
    const amount = (cents / 100).toFixed(fmt.decimals);
    const [intPart, decPart] = amount.split('.');
    const withThou = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, fmt.thouSep);
    return decPart !== undefined && fmt.decimals > 0 ? withThou + fmt.decSep + decPart : withThou;
  }

  /**
   * Swaps only the numeric portion of an element's original Shopify-rendered
   * text, leaving the currency symbol and any suffix untouched.
   * @param {Element|null} el
   * @param {number} cents
   */
  function paintEl(el, cents) {
    if (!el) return;
    if (!formatCache.has(el)) {
      const originalText = el.textContent || '';
      if (!/[\d]/.test(originalText)) return;
      formatCache.set(el, { originalText, fmt: detectNumberFormat(originalText) });
    }
    const { originalText, fmt } = formatCache.get(el);
    el.textContent = originalText.replace(/[\d][\d.,\s]*/, formatNumberOnly(cents, fmt));
  }

  /**
   * Renders an amount with the currency symbol and separators Shopify used for
   * this block's own price, so every market keeps its own formatting. Whole
   * amounts drop the ".00" -- the badge reads "Saved $90", not "Saved $90.00".
   * @param {Element} root
   * @param {number} cents
   * @returns {string|null} null when the price hasn't been read yet
   */
  function formatMoney(root, cents) {
    const cached = formatCache.get(getVisiblePriceEl(root));
    if (!cached) return null;
    const fmt = cached.fmt ? Object.assign({}, cached.fmt) : null;
    if (fmt && cents % 100 === 0) fmt.decimals = 0;
    return cached.originalText.replace(/[\d][\d.,\s]*/, formatNumberOnly(cents, fmt)).trim();
  }

  /**
   * Sits *after* <product-price>, not inside it: the block lays its children
   * out in a row (that's why "Subtotal:" is inline with the price), so
   * anything added inside would land on the same line.
   * @param {Element} root
   * @returns {HTMLElement}
   */
  function ensureSavingsEl(root) {
    const sibling = root.nextElementSibling;
    if (sibling instanceof HTMLElement && sibling.classList.contains(SAVINGS_CLASS)) return sibling;

    const el = document.createElement('div');
    el.className = SAVINGS_CLASS;
    // Kept in the DOM and toggled with [hidden] rather than removed, so the
    // live region survives and screen readers announce each new saving.
    el.setAttribute('aria-live', 'polite');
    el.hidden = true;
    el.innerHTML =
      '<span class="' + SAVINGS_CLASS + '__pill">' +
      TAG_ICON +
      '<span class="' + SAVINGS_CLASS + '__text"></span></span>';
    root.insertAdjacentElement('afterend', el);
    return el;
  }

  /**
   * "Saved $90" -- the gap between the two numbers on screen, which is what
   * the customer actually keeps whether it comes from the sale price, the
   * bulk tier, or both.
   * @param {Element} root
   * @param {number} savedCents
   */
  function renderSavings(root, savedCents) {
    const el = ensureSavingsEl(root);
    const money = savedCents > 0 ? formatMoney(root, savedCents) : null;
    if (!money) {
      el.hidden = true;
      return;
    }
    const label = root.dataset.savingsLabel || DEFAULT_SAVINGS_LABEL;
    el.querySelector('.' + SAVINGS_CLASS + '__text').textContent = label + ' ' + money;
    el.hidden = false;
  }

  /**
   * @param {Element} ctx
   * @param {number} subtotalCents  price for the whole quantity, after discounts
   * @param {number|null} baselineCents  undiscounted price for the whole quantity
   */
  function paint(ctx, subtotalCents, baselineCents) {
    setLoading(ctx, false);
    const saved = baselineCents != null ? baselineCents - subtotalCents : 0;

    ctx.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      paintEl(getVisiblePriceEl(root), subtotalCents);

      // Compare-at must scale with quantity too, otherwise the strikethrough
      // reads as a price increase (e.g. "Rs. 19,000 was Rs. 12,200").
      if (baselineCents != null) {
        root.querySelectorAll('.compare-at-price').forEach((el) => {
          paintEl(el, baselineCents);
        });
      }

      // After paintEl, which is what seeds the currency format cache.
      renderSavings(root, saved);
    });
  }

  /**
   * The "was" figure for one unit -- what the strikethrough shows, and so the
   * number the saving is measured against.
   *
   * The compare-at price wins whenever the product is on sale, so the saving
   * stays on one footing at every quantity instead of switching basis once a
   * bulk tier kicks in. That makes it the customer's whole saving off the list
   * price: sale plus bulk discount together.
   *
   * @param {Element} ctx
   * @param {Object|null} line  cart line, when we have one
   * @returns {number|null}
   */
  function baselineUnitCents(ctx, line) {
    const root = ctx.querySelector(ROOT_SELECTOR);
    const compareUnit = root ? parseInt(root.dataset.compareAtCents, 10) || 0 : 0;
    const unit = line && line.original_price
      ? line.original_price
      : (root ? parseInt(root.dataset.unitCents, 10) || 0 : 0);
    return Math.max(compareUnit, unit) || null;
  }

  /**
   * Fallback when the cart isn't involved: plain unit price x quantity.
   * @param {Element} ctx
   * @param {number} qty
   */
  function paintLocally(ctx, qty) {
    const root = ctx.querySelector(ROOT_SELECTOR);
    const unit = root ? parseInt(root.dataset.unitCents, 10) : NaN;
    if (!unit) {
      setLoading(ctx, false);
      return;
    }
    const baseline = baselineUnitCents(ctx, null);
    paint(ctx, unit * qty, baseline ? baseline * qty : null);
  }

  /* ------------------------------------------------------------------ *
   * Loading state -- mirrors the shimmer the cart drawer uses on its totals
   * ------------------------------------------------------------------ */

  function injectLoadingStyles() {
    if (document.getElementById('qty-price-loading-styles')) return;
    const style = document.createElement('style');
    style.id = 'qty-price-loading-styles';
    style.textContent = [
      '.' + LOADING_CLASS + '{',
      '  color: transparent !important;',
      '  background-image: linear-gradient(-85deg,',
      '    rgb(var(--color-foreground-rgb, 0 0 0) / 0.5) 10%,',
      '    rgb(var(--color-foreground-rgb, 0 0 0) / 0.12) 50%,',
      '    rgb(var(--color-foreground-rgb, 0 0 0) / 0.5) 90%);',
      '  background-clip: text;',
      '  -webkit-background-clip: text;',
      '  background-size: 200% 100%;',
      '  background-position: 100% 0;',
      '  animation: qty-price-shimmer 1.25s infinite linear;',
      '}',
      '@keyframes qty-price-shimmer { to { background-position: -100% 0; } }',
      '@media (prefers-reduced-motion: reduce) {',
      '  .' + LOADING_CLASS + ' { animation: none; opacity: 0.5; }',
      '}',
      '.' + SAVINGS_CLASS + '{',
      '  margin: -6px 0 0;',
      '  padding: 0;',
      '  width: 100%;',
      '  flex-basis: 100%;',
      '  display: flex;',
      '  justify-content: flex-end;',
      '}',
      '.' + SAVINGS_CLASS + '[hidden]{ display: none; }',
      '.' + SAVINGS_CLASS + '__pill{',
      '  display: inline-flex;',
      '  align-items: center;',
      '  gap: 0.4em;',
      '  padding: 0.3em 0.75em;',
      '  border: 1px solid currentColor;',
      '  border-radius: 999px;',
      '  font-size: 0.8125em;',
      '  line-height: 1.3;',
      '  white-space: nowrap;',
      '  color: var(--qty-price-savings-color, #16a34a);',
      '}',
      '.' + SAVINGS_CLASS + '__icon{',
      '  width: 1.15em;',
      '  height: 1.15em;',
      '  flex: none;',
      '}',
      '.' + LOADING_CLASS + '-savings{ opacity: 0.35; }',
      // The popup stacks the badge below the price rather than wrapping it
      // onto the price row, so it needs its own spacing.
      // !important so the popup's own layout rules can't win over these.
      '#quick-add-modal-content .' + SAVINGS_CLASS + '{',
      '  margin-top: 8px !important;',
      '  margin-bottom: 20px !important;',
      '  justify-content: flex-end !important;',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  /**
   * @param {Element} ctx
   * @param {boolean} on
   */
  function setLoading(ctx, on) {
    ctx.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const targets = [getVisiblePriceEl(root)].concat(
        Array.prototype.slice.call(root.querySelectorAll('.compare-at-price'))
      );
      targets.forEach((el) => {
        if (el) el.classList.toggle(LOADING_CLASS, on);
      });

      // The badge holds the previous quantity's number until the new one
      // lands, so fade it rather than shimmering unrelated digits.
      const sibling = root.nextElementSibling;
      if (sibling instanceof HTMLElement && sibling.classList.contains(SAVINGS_CLASS)) {
        sibling.classList.toggle(LOADING_CLASS + '-savings', on);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart
   * ------------------------------------------------------------------ */

  function getCart() {
    return fetch(CART_URL + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  /**
   * Sets the absolute quantity for a variant. /cart/update adds when absent
   * and removes at 0, and returns the full cart, so one call covers every case.
   *
   * Deliberately does NOT request sections and no cart event is dispatched --
   * this is a throwaway measurement, so the drawer must not open, the cart
   * badge must not flicker, and the quick-add popup must not close itself.
   *
   * @param {string} variantId
   * @param {number} quantity
   */
  function setCartQuantity(variantId, quantity) {
    const updates = {};
    updates[variantId] = quantity;
    return fetch(CART_UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    }).then((r) => r.json());
  }

  function findLine(cart, variantId) {
    if (!cart || !cart.items) return null;
    return cart.items.find((item) => String(item.variant_id) === String(variantId)) || null;
  }

  /**
   * Takes the probe back out of the cart.
   *
   * Subtracts what we put in rather than writing the old number back: if the
   * customer pressed Add to cart while the lookup was still in flight, an
   * absolute write would throw their items away. Reading the cart first costs
   * one request and makes the restore safe whatever landed in between.
   *
   * @param {string} variantId
   * @param {number} probedQty  quantity the cart actually held for the probe
   * @param {number} baseline   quantity it held before
   */
  async function restoreCart(variantId, probedQty, baseline) {
    const added = probedQty - baseline;
    if (added === 0) return;

    let target = baseline;
    try {
      const line = findLine(await getCart(), variantId);
      target = Math.max(0, (line ? line.quantity : 0) - added);
    } catch (error) {
      // Cart unreadable -- the old number is still the best guess.
    }
    await setCartQuantity(variantId, target);
  }

  function lineBaselineTotal(ctx, line, qty) {
    const unit = baselineUnitCents(ctx, line);
    return unit ? unit * qty : null;
  }

  /* ------------------------------------------------------------------ *
   * Main update cycle
   * ------------------------------------------------------------------ */

  /**
   * True once the customer has moved on to another quantity. Cart round trips
   * are slow enough that a 5 -> 1 change lands while the five-unit lookup is
   * still in flight; without this the slower answer wins and the price sticks.
   * @param {Element} ctx
   * @param {number} seq
   */
  function isStale(ctx, seq) {
    return stateFor(ctx).seq !== seq;
  }

  function enqueue(ctx, seq, task) {
    chain = chain.then(task).catch((error) => {
      if (isStale(ctx, seq)) return;
      setLoading(ctx, false);
      console.warn(TAG, 'price lookup failed, showing unit price x quantity', error);
      paintLocally(ctx, getQuantity(ctx));
    });
    return chain;
  }

  /** @param {Element} ctx */
  function run(ctx) {
    if (!ctx.isConnected) return;

    const seq = stateFor(ctx).seq;
    const qty = getQuantity(ctx);
    const variantId = getVariantId(ctx);

    if (!variantId) {
      setLoading(ctx, false);
      // No size chosen yet: warn exactly as Add to cart does and leave the
      // price untouched rather than showing a total the customer can't buy.
      if (!requireVariantSelection(ctx)) return;
      paintLocally(ctx, qty);
      return;
    }

    if (qty < SYNC_MIN_QTY) {
      paintLocally(ctx, qty);
      return;
    }

    enqueue(ctx, seq, async () => {
      const cart = await getCart();
      const existing = findLine(cart, variantId);
      const baseline = existing ? existing.quantity : 0;

      // The customer genuinely has this quantity in their cart already, so we
      // can read the price straight off it without touching anything.
      if (existing && baseline === qty) {
        if (!isStale(ctx, seq)) paint(ctx, existing.final_line_price, lineBaselineTotal(ctx, existing, qty));
        return;
      }

      pendingRestore = { variantId: variantId, baseline: baseline };
      /** Quantity the cart really ended up holding, once we've heard back. */
      let probedQty = null;
      try {
        const probed = await setCartQuantity(variantId, qty);
        const line = findLine(probed, variantId);
        probedQty = line ? line.quantity : 0;

        if (isStale(ctx, seq)) return;
        if (line) {
          paint(ctx, line.final_line_price, lineBaselineTotal(ctx, line, qty));
        } else {
          paintLocally(ctx, qty);
        }
      } finally {
        // Always put the cart back, even if the read above threw or a newer
        // quantity made this lookup irrelevant.
        if (probedQty === null) {
          // The probe's own answer never arrived, so we don't know whether it
          // applied. Writing the old number back is the safer guess.
          await setCartQuantity(variantId, baseline);
        } else {
          await restoreCart(variantId, probedQty, baseline);
        }
        pendingRestore = null;
      }
    });
  }

  /**
   * Shimmer goes on straight away, before the debounce, so the price reacts
   * the instant the quantity changes even though the real number needs a
   * round trip to Shopify.
   * @param {Element} ctx
   */
  function schedule(ctx) {
    const state = stateFor(ctx);
    state.seq += 1;
    if (getQuantity(ctx) >= SYNC_MIN_QTY && getVariantId(ctx)) setLoading(ctx, true);
    clearTimeout(state.timer);
    state.timer = setTimeout(() => run(ctx), DEBOUNCE_MS);
  }

  /** @param {EventTarget | null} target */
  function scheduleFrom(target) {
    const ctx = contextFor(target);
    if (ctx) schedule(ctx);
  }

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */

  document.addEventListener('quantity-selector:update', (e) => scheduleFrom(e.target));

  document.addEventListener('change', (e) => {
    if (e.target instanceof HTMLElement && e.target.matches(QTY_INPUT_SELECTOR)) scheduleFrom(e.target);
  });

  document.addEventListener('input', (e) => {
    if (e.target instanceof HTMLElement && e.target.matches(QTY_INPUT_SELECTOR)) scheduleFrom(e.target);
  });

  // Insurance: the +/- buttons set input.value as a property, which fires
  // neither `input` nor `change`, so rely on the click as well as the
  // component's own event.
  document.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('quantity-selector-component')) scheduleFrom(e.target);
  });

  // product-price.js replaces the price nodes on every variant swap. It swaps
  // only the inner price container, leaving our data-* attributes pointing at
  // the old variant, so copy the new figures across from the same rendered
  // HTML it uses. (The format cache is keyed by element, so the replaced
  // nodes bring their own fresh entries.)
  document.addEventListener('variant:update', (event) => {
    const ctx = contextFor(event.target);
    if (!ctx) return;

    const html = event.detail && event.detail.data && event.detail.data.html;
    if (html) {
      ctx.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
        const fresh = html.querySelector('product-price[data-block-id="' + root.dataset.blockId + '"]');
        if (!fresh) return;
        root.dataset.unitCents = fresh.dataset.unitCents || '';
        root.dataset.compareAtCents = fresh.dataset.compareAtCents || '';
      });
    }

    schedule(ctx);
  });

  /**
   * The quick-add popup keeps one dialog and swaps a clone of the product
   * page into it, so a second product can inherit the badge left over from
   * the first. Repaint from the freshly cloned markup every time it opens.
   *
   * `dialog:open` is fired on the dialog element and does not bubble, so it
   * has to be listened for there rather than on the document.
   */
  function watchQuickAddDialog() {
    const dialog = document.getElementById('quick-add-dialog');
    if (!dialog) return;

    dialog.addEventListener('dialog:open', () => {
      const modal = dialog.querySelector(MODAL_SELECTOR);
      if (modal && modal.querySelector(ROOT_SELECTOR)) schedule(modal);
    });
  }

  /**
   * If the page is torn down mid-probe the cart would keep the probe
   * quantity, so restore it with a beacon that survives unload.
   */
  function restoreOnUnload() {
    if (!pendingRestore) return;
    const updates = {};
    updates[pendingRestore.variantId] = pendingRestore.baseline;
    const body = JSON.stringify({ updates });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(CART_UPDATE_URL, new Blob([body], { type: 'application/json' }));
    }
    pendingRestore = null;
  }

  window.addEventListener('pagehide', restoreOnUnload);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') restoreOnUnload();
  });

  function init() {
    injectLoadingStyles();
    watchQuickAddDialog();
    allContexts().forEach((ctx) => paintLocally(ctx, getQuantity(ctx)));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
